import React, { useState, useRef, useEffect } from 'react';
import type { PoleImageType, Coordinate2D } from '../types';

interface DraggableReferenceLineProps {
  poleImage: PoleImageType;
  topPoint: Coordinate2D;
  bottomPoint: Coordinate2D;
  imageScale: number;
  containerTransform: { x: number; y: number; scale: number };
  initialHorizontalOffset: number;
  initialVerticalOffset: number;
  onTopPointDrag: (x: number, y: number) => void;
  onBottomPointDrag: (x: number, y: number) => void;
}

export const DraggableReferenceLine: React.FC<DraggableReferenceLineProps> = ({
  poleImage,
  topPoint,
  bottomPoint,
  imageScale,
  containerTransform,
  initialHorizontalOffset,
  initialVerticalOffset,
  onTopPointDrag,
  onBottomPointDrag
}) => {
  const [isDraggingTop, setIsDraggingTop] = useState(false);
  const [isDraggingBottom, setIsDraggingBottom] = useState(false);
  const dragRef = useRef<'top' | 'bottom' | null>(null);
  
  // Convert image coordinates to rendered coordinates (within the overlay container)
  // NO OFFSETS because we're already inside the positioned overlay container
  const screenTop = {
    x: topPoint.x * imageScale,
    y: topPoint.y * imageScale
  };
  
  const screenBottom = {
    x: bottomPoint.x * imageScale,
    y: bottomPoint.y * imageScale
  };

  // Calculate the length and angle of the line
  const dx = screenTop.x - screenBottom.x;
  const dy = screenTop.y - screenBottom.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx) * 180 / Math.PI;

  // Function to calculate x for a given y along the reference line
  const calculateXforY = (y: number): number => {
    const refTop = poleImage.referenceLine?.top.pixelCoordinates;
    const refBottom = poleImage.referenceLine?.bottom.pixelCoordinates;
    
    if (!refTop || !refBottom) {
      return topPoint.x; // Fallback
    }
    
    // If the line is vertical or nearly vertical
    if (Math.abs(refTop.y - refBottom.y) < 0.001) {
      return refTop.x;
    }
    
    // Calculate slope and y-intercept
    const slope = (refTop.x - refBottom.x) / (refTop.y - refBottom.y);
    const yIntercept = refBottom.x - (slope * refBottom.y);
    
    // Calculate x for the given y
    return slope * y + yIntercept;
  };

  // Handle dragging for the top point
  const handleTopMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dragRef.current) return; // Already dragging
    
    dragRef.current = 'top';
    setIsDraggingTop(true);
    
    const startY = e.clientY;
    const startPointY = topPoint.y;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (dragRef.current !== 'top') return;
      
      // Calculate the change in Y position in screen pixels
      const deltaY = e.clientY - startY;
      
      // Convert pixel movement to image coordinate system
      // Scale by both imageScale and containerTransform.scale
      const imageDeltaY = deltaY / (imageScale * containerTransform.scale);
      
      // Calculate the new Y position in the image coordinate system
      const newY = startPointY + imageDeltaY;
      
      // Calculate the corresponding X position along the reference line
      const newX = calculateXforY(newY);
      
      onTopPointDrag(newX, newY);
    };
    
    const handleMouseUp = () => {
      setIsDraggingTop(false);
      dragRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Handle dragging for the bottom point
  const handleBottomMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dragRef.current) return; // Already dragging
    
    dragRef.current = 'bottom';
    setIsDraggingBottom(true);
    
    const startY = e.clientY;
    const startPointY = bottomPoint.y;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (dragRef.current !== 'bottom') return;
      
      // Calculate the change in Y position in screen pixels
      const deltaY = e.clientY - startY;
      
      // Convert pixel movement to image coordinate system
      const imageDeltaY = deltaY / (imageScale * containerTransform.scale);
      
      // Calculate the new Y position in the image coordinate system
      const newY = startPointY + imageDeltaY;
      
      // Calculate the corresponding X position along the reference line
      const newX = calculateXforY(newY);
      
      onBottomPointDrag(newX, newY);
    };
    
    const handleMouseUp = () => {
      setIsDraggingBottom(false);
      dragRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dragRef.current = null;
    };
  }, []);

  const handleSize = 40 / containerTransform.scale;
  const handleOffset = 60 / containerTransform.scale;

  return (
    <>
      {/* The reference line */}
      <div
        style={{
          position: 'absolute',
          left: `${screenBottom.x}px`,
          top: `${screenBottom.y}px`,
          width: `${length}px`,
          height: `${4 / containerTransform.scale}px`,
          backgroundColor: 'yellow',
          transform: `rotate(${angle}deg)`,
          transformOrigin: 'left center',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      />
      
      {/* Top drag handle */}
      <div
        style={{
          position: 'absolute',
          left: `${screenTop.x}px`,
          top: `${screenTop.y}px`,
          width: `${handleSize}px`,
          height: `${handleSize}px`,
          backgroundColor: isDraggingTop ? 'orange' : 'yellow',
          borderRadius: '50%',
          border: isDraggingTop ? '3px solid red' : '2px solid black',
          transform: `translate(-50%, -50%) scale(${1 / containerTransform.scale})`,
          transformOrigin: 'center',
          cursor: isDraggingTop ? 'grabbing' : 'grab',
          zIndex: 1000,
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isDraggingTop 
            ? '0 0 10px rgba(255, 0, 0, 0.5)' 
            : '0 2px 4px rgba(0, 0, 0, 0.3)',
          transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
        }}
        onMouseDown={handleTopMouseDown}
      >
        <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'black',
          userSelect: 'none',
        }}>↕</div>
      </div>
      
      {/* Top label */}
      <div
        style={{
          position: 'absolute',
          left: `${screenTop.x + handleOffset}px`,
          top: `${screenTop.y}px`,
          transform: `translate(0, -50%) scale(${1 / containerTransform.scale})`,
          transformOrigin: 'left center',
          zIndex: 999,
          color: 'yellow',
          fontWeight: 'bold',
          fontSize: '16px',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
          padding: '5px',
          pointerEvents: 'none',
        }}
      >
        Top
      </div>
      
      {/* Bottom drag handle */}
      <div
        style={{
          position: 'absolute',
          left: `${screenBottom.x}px`,
          top: `${screenBottom.y}px`,
          width: `${handleSize}px`,
          height: `${handleSize}px`,
          backgroundColor: isDraggingBottom ? 'orange' : 'yellow',
          borderRadius: '50%',
          border: isDraggingBottom ? '3px solid red' : '2px solid black',
          transform: `translate(-50%, -50%) scale(${1 / containerTransform.scale})`,
          transformOrigin: 'center',
          cursor: isDraggingBottom ? 'grabbing' : 'grab',
          zIndex: 1000,
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isDraggingBottom 
            ? '0 0 10px rgba(255, 0, 0, 0.5)' 
            : '0 2px 4px rgba(0, 0, 0, 0.3)',
          transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
        }}
        onMouseDown={handleBottomMouseDown}
      >
        <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'black',
          userSelect: 'none',
        }}>↕</div>
      </div>
      
      {/* Bottom label */}
      <div
        style={{
          position: 'absolute',
          left: `${screenBottom.x + handleOffset}px`,
          top: `${screenBottom.y}px`,
          transform: `translate(0, -50%) scale(${1 / containerTransform.scale})`,
          transformOrigin: 'left center',
          zIndex: 999,
          color: 'yellow',
          fontWeight: 'bold',
          fontSize: '16px',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
          padding: '5px',
          pointerEvents: 'none',
        }}
      >
        Bottom
      </div>
      
      {/* Horizontal line at top */}
      <div
        style={{
          position: 'absolute',
          left: '0',
          right: '0',
          top: `${screenTop.y}px`,
          height: `${2 / containerTransform.scale}px`,
          backgroundColor: 'rgba(255, 255, 0, 0.3)',
          zIndex: 4,
          pointerEvents: 'none',
        }}
      />
      
      {/* Horizontal line at bottom */}
      <div
        style={{
          position: 'absolute',
          left: '0',
          right: '0',
          top: `${screenBottom.y}px`,
          height: `${2 / containerTransform.scale}px`,
          backgroundColor: 'rgba(255, 255, 0, 0.3)',
          zIndex: 4,
          pointerEvents: 'none',
        }}
      />
      
      {/* Instruction text */}
      <div 
        style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: `translateX(-50%) scale(${1 / containerTransform.scale})`,
          transformOrigin: 'center top',
          backgroundColor: 'rgba(255, 255, 0, 0.9)',
          color: 'black',
          padding: '8px 16px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          zIndex: 1001,
          fontWeight: 'bold',
          fontSize: '14px',
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        Drag the handles to adjust pole top and bottom
      </div>
    </>
  );
};